import { useEffect } from 'react';

interface SEOProps {
  title: string;
  description: string;
  image?: string;
  url?: string;
  type?: 'website' | 'article';
  publishedAt?: string;
  author?: string;
  keywords?: string;
  noIndex?: boolean;
}

function setMeta(property: string, content: string, attr: 'name' | 'property' = 'name') {
  let el = document.querySelector(`meta[${attr}="${property}"]`) as HTMLMetaElement;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, property);
    document.head.appendChild(el);
  }
  el.content = content;
}

function setLink(rel: string, href: string) {
  let el = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement;
  if (!el) {
    el = document.createElement('link');
    el.rel = rel;
    document.head.appendChild(el);
  }
  el.href = href;
}

export default function SEO({ title, description, image, url, type = 'website', publishedAt, author, keywords, noIndex }: SEOProps) {
  useEffect(() => {
    const desc = description.length > 160 ? description.substring(0, 157) + '...' : description;
    const pageUrl = url || window.location.href;
    const defaultImage = '/og-default.jpg';
    const ogImage = image || defaultImage;

    // Title
    document.title = title.includes('Apex DeFi Labs Inc.') ? title : `${title} | Apex DeFi Labs Inc.`;

    // Meta
    setMeta('description', desc);
    if (keywords) setMeta('keywords', keywords);
    setMeta('robots', noIndex ? 'noindex, nofollow' : 'index, follow');

    // Canonical
    setLink('canonical', pageUrl);

    // Open Graph
    setMeta('og:title', title, 'property');
    setMeta('og:description', desc, 'property');
    setMeta('og:image', ogImage, 'property');
    setMeta('og:url', pageUrl, 'property');
    setMeta('og:type', type, 'property');
    setMeta('og:site_name', 'Apex DeFi Labs Inc.', 'property');

    // Twitter
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', title);
    setMeta('twitter:description', desc);
    setMeta('twitter:image', ogImage);

    // Article
    if (type === 'article') {
      if (publishedAt) setMeta('article:published_time', publishedAt, 'property');
      setMeta('article:author', author || 'Apex DeFi Labs Inc.', 'property');
      setMeta('article:section', 'Finance & Technology', 'property');
    }
  }, [title, description, image, url, type, publishedAt, author, keywords, noIndex]);

  return null;
}
